import { useEffect, useState } from "react";
import { api, brl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer, { SocialButtons } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Coins, Search, History as HistoryIcon, TrendingUp, Sparkles, X } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [preview, setPreview] = useState([]);
  const [history, setHistory] = useState([]);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    api.get("/vehicles/preview?limit=6").then((r) => setPreview(r.data.vehicles)).catch(() => {});
    api.get("/history").then((r) => setHistory(r.data.history || [])).catch(() => {});
    // Show community welcome for brand-new signups
    const seen = localStorage.getItem("lr_seen_community");
    if (!seen) {
      setShowWelcome(true);
      localStorage.setItem("lr_seen_community", "1");
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {showWelcome && (
          <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-emerald-500/15 via-slate-900 to-fuchsia-500/10 border border-emerald-500/30 relative overflow-hidden" data-testid="welcome-community-banner">
            <button onClick={() => setShowWelcome(false)} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-slate-800/60 hover:bg-slate-700 flex items-center justify-center" data-testid="dismiss-welcome-btn">
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-emerald-400">🎉 Bem-vindo à comunidade</div>
                <h3 className="mt-1 font-display text-xl font-bold text-white">Não perca nenhuma oportunidade</h3>
                <p className="mt-1 text-sm text-slate-300 max-w-lg">Entre no canal exclusivo do WhatsApp pra receber lotes escolhidos a dedo e siga nosso Instagram pra dicas de lance.</p>
              </div>
              <SocialButtons />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-400">Dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-1">Olá, {user?.name || "Investidor"}</h1>
            <p className="text-slate-400 mt-1 text-sm">Sua central de inteligência para leilões judiciais.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/search"><Button className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" data-testid="dashboard-search-cta"><Search className="w-4 h-4 mr-2" />Nova pesquisa</Button></Link>
            <Link to="/store"><Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="dashboard-store-cta"><Coins className="w-4 h-4 mr-2" />Créditos</Button></Link>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border border-emerald-500/30" data-testid="stat-credits">
            <Coins className="w-5 h-5 text-emerald-400" />
            <div className="mt-3 font-mono text-3xl font-bold text-white">{user?.credits ?? 0}</div>
            <div className="text-xs text-emerald-300/80 uppercase tracking-widest mt-1">Créditos disponíveis</div>
          </div>
          <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800" data-testid="stat-searches">
            <HistoryIcon className="w-5 h-5 text-amber-400" />
            <div className="mt-3 font-mono text-3xl font-bold text-white">{history.length}</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest mt-1">Pesquisas realizadas</div>
          </div>
          <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800" data-testid="stat-avg-score">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            <div className="mt-3 font-mono text-3xl font-bold text-white">
              {preview.length ? Math.round(preview.reduce((s, v) => s + v.score, 0) / preview.length) : 0}
            </div>
            <div className="text-xs text-slate-400 uppercase tracking-widest mt-1">Score médio hoje</div>
          </div>
        </div>

        {/* Top deals preview */}
        <div className="mt-10">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-400" />Melhores oportunidades agora</h2>
            <Link to="/search" className="text-sm text-emerald-400 hover:text-emerald-300">Explorar →</Link>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {preview.slice(0, 6).map((v) => (
              <div key={v.id} className="p-4 rounded-xl bg-slate-900/70 border border-slate-800 card-hover" data-testid={`dash-preview-${v.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xs font-mono text-slate-500">{v.uf}</div>
                    <div className="font-semibold text-white mt-0.5">{v.brand} {v.model}</div>
                    <div className="text-xs text-slate-400">{v.year}</div>
                  </div>
                  <div className="deal-score">{v.score}</div>
                </div>
                <div className="mt-3 flex justify-between items-baseline">
                  <span className="font-mono text-emerald-400 font-bold">{brl(v.min_bid)}</span>
                  <span className="font-mono text-xs text-slate-500 line-through">{brl(v.fipe_value)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
